import { PILLAR_ARTICLES } from "@/mocks/articlesMeta";
import { ARTICLES_EN } from "@/mocks/articlesEn";
import { ARTICLES_FR } from "@/mocks/articlesFr";
import { ARTICLES_DA } from "@/mocks/articlesDa";
import { ARTICLES_DE } from "@/mocks/articlesDe";
import { SITE_URL, SITE_NAME, FAVICON } from "./config";

interface ArticleSummary {
  title: string;
  excerpt: string;
}

const CONTENT_MAPS: Record<string, Record<string, ArticleSummary>> = {
  en: ARTICLES_EN as unknown as Record<string, ArticleSummary>,
  fr: ARTICLES_FR as unknown as Record<string, ArticleSummary>,
  da: ARTICLES_DA as unknown as Record<string, ArticleSummary>,
  de: ARTICLES_DE as unknown as Record<string, ArticleSummary>,
};

function pickLang(language: string): string {
  if (language?.startsWith("fr")) return "fr";
  if (language?.startsWith("da")) return "da";
  if (language?.startsWith("de")) return "de";
  return "en";
}

function organizationSchema() {
  return {
    "@type": "Organization",
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: {
      "@type": "ImageObject",
      url: FAVICON,
    },
  };
}

function webSiteSchema() {
  return {
    "@type": "WebSite",
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    inLanguage: ["en", "fr", "da", "de"],
  };
}

function articleSchema(title: string, description: string, image: string, lang: string, path: string) {
  return {
    "@type": "Article",
    headline: title,
    description,
    image: [image],
    datePublished: "2026-08-01",
    dateModified: "2026-08-25",
    inLanguage: lang,
    author: organizationSchema(),
    publisher: organizationSchema(),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}${path}`,
    },
  };
}

function breadcrumbSchema(name: string, path: string) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${SITE_URL}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name,
        item: `${SITE_URL}${path}`,
      },
    ],
  };
}

function wrap(schema: Record<string, unknown>) {
  return { "@context": "https://schema.org", ...schema };
}

export function resolveSchemas(pathname: string, language: string): object[] {
  if (pathname === "/") {
    return [wrap(webSiteSchema()), wrap(organizationSchema())];
  }

  if (pathname === "/spreadsheet-guide") {
    const lang = pickLang(language);
    const content = CONTENT_MAPS[lang]?.review ?? CONTENT_MAPS.en.review;
    const title = "AllChinaBuy Spreadsheet: The Complete Beginner Guide";
    const guideImage =
      "https://readdy.ai/api/search-image?query=Overhead%20view%20of%20a%20tidy%20desk%20with%20a%20printed%20product%20category%20checklist%20a%20pencil%20and%20folded%20clothing%20fabric%20swatches%20on%20a%20warm%20sage%20green%20and%20cream%20background%20editorial%20still%20life%20for%20a%20shopping%20guide%20high%20detail&width=1600&height=900&seq=hero-article&orientation=landscape&nocache=true";
    return [
      wrap(
        articleSchema(
          title,
          content?.excerpt ??
            "Learn what the AllChinaBuy Spreadsheet is and how to use it to find products and sellers.",
          guideImage,
          lang,
          "/spreadsheet-guide",
        ),
      ),
      wrap(breadcrumbSchema(title, "/spreadsheet-guide")),
    ];
  }

  const articleMatch = pathname.match(/^\/article\/([^/]+)$/);
  if (articleMatch) {
    const slug = articleMatch[1];
    const meta = PILLAR_ARTICLES.find((a) => a.slug === slug);
    if (meta) {
      const lang = pickLang(language);
      const content = CONTENT_MAPS[lang]?.[slug] ?? CONTENT_MAPS.en[slug];
      const title = content?.title ?? SITE_NAME;
      return [
        wrap(
          articleSchema(
            `${title} | ${SITE_NAME}`,
            content?.excerpt ?? "",
            meta.image,
            lang,
            `/article/${slug}`,
          ),
        ),
        wrap(breadcrumbSchema(title, `/article/${slug}`)),
      ];
    }
  }

  return [];
}